/**
 * Read-only banner + blocked-gate modal for the similarity triage page.
 *
 * Renders three visual states driven by `useBatchReviewLock`:
 *   • status='blocked'  → full-screen gate modal: [Read-only view] [Take over]
 *   • status='readonly' → sticky banner across the top: "Read-only —
 *                          {holder} is reviewing this batch. [Take over]"
 *   • status='stolen'   → sticky red banner: "{new holder} took over
 *                          your session. [Take over back] [Continue read-only]"
 *
 * Held/idle/acquiring states render nothing (silent).
 */
import React from 'react';
import { Button } from '../../../components/ui/button';
import { Eye, UserX, ShieldAlert } from 'lucide-react';

function formatRelative(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  return `${hrs} hr ago`;
}

export default function BatchReviewLockGate({
  status,
  activeLock,
  onEnterReadOnly,
  onTakeOver,
}) {
  if (status === 'blocked') {
    const holder = activeLock?.user_name || 'Another admin';
    const started = formatRelative(activeLock?.acquired_at);
    return (
      <div
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        data-testid="batch-lock-blocked-modal"
      >
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-amber-100 p-2 flex-shrink-0">
              <UserX className="w-5 h-5 text-amber-700" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900">
                Another admin is reviewing this batch
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                <span className="font-medium text-gray-900">{holder}</span>{' '}
                started {started}. Two people editing the same batch can
                collide on Apply — pick how you'd like to proceed.
              </p>
            </div>
          </div>
          <div className="mt-5 flex flex-col gap-2">
            <Button
              onClick={onTakeOver}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
              data-testid="batch-lock-take-over-btn"
            >
              Take over ({holder} will lose their session)
            </Button>
            <Button
              variant="outline"
              onClick={onEnterReadOnly}
              className="w-full"
              data-testid="batch-lock-read-only-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              Read-only view
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'readonly') {
    const holder = activeLock?.user_name || 'Another admin';
    return (
      <div
        className="sticky top-0 z-40 bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-3"
        data-testid="batch-lock-readonly-banner"
      >
        <Eye className="w-4 h-4 text-amber-700 flex-shrink-0" />
        <div className="text-sm text-amber-900 min-w-0 flex-1">
          <span className="font-semibold">Read-only</span> — {holder} is
          reviewing this batch. Mutation actions are disabled.
        </div>
        <Button
          size="sm"
          variant="outline"
          className="border-amber-400 text-amber-900 hover:bg-amber-100"
          onClick={onTakeOver}
          data-testid="batch-lock-readonly-take-over-btn"
        >
          Take over
        </Button>
      </div>
    );
  }

  if (status === 'stolen') {
    const holder = activeLock?.user_name || 'Another admin';
    return (
      <div
        className="sticky top-0 z-40 bg-red-50 border-b border-red-200 px-4 py-2 flex items-center gap-3"
        data-testid="batch-lock-stolen-banner"
      >
        <ShieldAlert className="w-4 h-4 text-red-700 flex-shrink-0" />
        <div className="text-sm text-red-900 min-w-0 flex-1">
          <span className="font-semibold">Session ended</span> — {holder} took
          over this batch. Any unsaved decisions on this tab are stale.
        </div>
        <Button
          size="sm"
          className="bg-red-600 hover:bg-red-700 text-white"
          onClick={onTakeOver}
          data-testid="batch-lock-stolen-take-over-btn"
        >
          Take back
        </Button>
      </div>
    );
  }

  return null;
}
